import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Clock, ShieldAlert, XCircle } from 'lucide-react';
import API from '../api/api';

const STATUS_BADGES = {
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  executed: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-200 text-gray-700',
  failed: 'bg-red-100 text-red-800',
};

const ACTION_LABELS = {
  book_pickup: 'Book Opendock pickup',
  reschedule_pickup: 'Reschedule pickup',
  send_lagrou_request: 'Send Lagrou pickup request',
  update_load: 'Update load from email',
};

// An update the agent applied on its own (no human decision) reads as "auto-applied".
function isAutoApplied(action) {
  return action.action_type === 'update_load' && action.status === 'executed' && !action.decided_by;
}

function StatusBadge({ action }) {
  const status = isAutoApplied(action) ? 'auto_applied' : action.status;
  const cls = status === 'auto_applied'
    ? 'bg-emerald-100 text-emerald-800'
    : STATUS_BADGES[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

// Load changes read from a CC'd email: what was (or would be) overwritten, record by
// record — applied values once executed, the planned preview while pending.
function LoadUpdateDetails({ action }) {
  const p = action.params || {};
  const rows = action.result?.applied || p.preview || [];
  const email = p.email || {};
  return (
    <div className="mt-1.5 space-y-1.5">
      {rows.length > 0 && (
        <table className="text-xs border border-gray-200 rounded">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              {['Record', 'Field', 'Before', 'After'].map(h => (
                <th key={h} className="px-2 py-1 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.target}-${r.id}-${r.field}-${i}`} className="border-t border-gray-100">
                <td className="px-2 py-1 text-gray-600">{r.record || r.target}</td>
                <td className="px-2 py-1 text-gray-600">{r.field_label || r.field}</td>
                <td className="px-2 py-1 text-gray-400 line-through">{r.before ?? '—'}</td>
                <td className="px-2 py-1 text-gray-900 font-semibold">{r.after ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(email.sender || email.subject) && (
        <div className="text-xs text-gray-500">
          From email{email.sender ? ` by ${email.sender}` : ''}{email.subject ? ` — “${email.subject}”` : ''}
          {p.source && p.source !== 'email body' ? ` (${p.source})` : ''}
        </div>
      )}
      {p.evidence && (
        <blockquote className="text-xs text-gray-600 italic border-l-2 border-gray-300 pl-2">
          {p.evidence}
        </blockquote>
      )}
      {action.status === 'pending_approval' && p.review_reasons?.length > 0 && (
        <div className="text-xs text-amber-700 font-medium">
          Needs review: {p.review_reasons.join('; ')}
        </div>
      )}
    </div>
  );
}

// '2026-07-22T09:00:00-05:00' → 'Wed, Jul 22 at 9:00 AM' — the value is already
// warehouse-local wall time, so the offset is dropped rather than converted.
function formatSlotLocal(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || '');
  if (!m) return iso || '';
  const dt = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const day = dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const hour12 = (dt.getHours() % 12) || 12;
  const ampm = dt.getHours() < 12 ? 'AM' : 'PM';
  return `${day} at ${hour12}:${String(dt.getMinutes()).padStart(2, '0')} ${ampm}`;
}

// '2026-07-22T09:00:00-05:00' → '2026-07-22T09:00' for a datetime-local input.
// The offset is dropped: the value is already warehouse-local wall time.
function isoToLocalInput(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || '');
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}` : '';
}

// Reviewer-facing facts captured when the action was proposed (params.details).
function ActionDetails({ action }) {
  if (action.action_type === 'update_load') return <LoadUpdateDetails action={action} />;
  const d = action.params?.details;
  if (!d) return null;

  const rows = [];
  if (d.proposed_slot_local) {
    rows.push(['Proposed time', `${formatSlotLocal(d.proposed_slot_local)} (warehouse local)`, true]);
  } else if (d.pickup_date) {
    const time = d.requested_time || d.preferred_time;
    rows.push([
      action.action_type === 'send_lagrou_request' ? 'Requesting' : 'Target',
      `${d.pickup_date}${time ? ` at ${time}` : ''}`,
      true,
    ]);
  }
  if (d.warehouse || d.facility) rows.push(['Facility', d.warehouse || d.facility]);
  if (d.pallets) rows.push(['Pallets', d.pallets]);
  if (d.pos?.length) rows.push(['POs', d.pos.join(', ')]);
  if (d.recipients?.length) rows.push(['Email to', d.recipients.join(', ')]);
  if (!rows.length && !d.slot_note) return null;

  return (
    <dl className="mt-1.5 space-y-0.5">
      {rows.map(([label, value, emphasize]) => (
        <div key={label} className="flex gap-1.5 text-xs">
          <dt className="text-gray-400 shrink-0">{label}:</dt>
          <dd className={emphasize ? 'text-gray-900 font-semibold' : 'text-gray-600'}>{value}</dd>
        </div>
      ))}
      {d.slot_note && (
        <div className={`text-xs ${/REVIEW/i.test(d.slot_note) ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
          {d.slot_note}
        </div>
      )}
    </dl>
  );
}

function AgentApprovals() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deciding, setDeciding] = useState(null); // action id in flight
  const [message, setMessage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(null);        // action id being edited
  const [editValue, setEditValue] = useState('');      // 'YYYY-MM-DDTHH:MM' (warehouse-local)
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editUnavailable, setEditUnavailable] = useState(null); // {detail, available_slots, ...}

  const fetchActions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get('agent/actions/');
      const data = response.data.results || response.data;
      setActions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Failed to load agent actions:', err);
      setError('Failed to load agent actions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const decide = async (action, decision) => {
    let payload = {};
    if (decision === 'reject') {
      const reason = window.prompt('Reason for rejecting this action (optional):') || '';
      payload = { reason };
    }
    try {
      setDeciding(action.id);
      setMessage(null);
      await API.post(`agent/actions/${action.id}/${decision}/`, payload);
      setMessage(
        decision === 'approve'
          ? `Approved: "${action.summary}" — the agent is executing it now.`
          : action.action_type === 'update_load'
            ? `Rejected: "${action.summary}" — the load was left unchanged.`
            : `Rejected: "${action.summary}" — the conversation was escalated to a human.`
      );
      await fetchActions();
    } catch (err) {
      console.error(`Failed to ${decision} action:`, err);
      setError(err.response?.data?.detail || `Failed to ${decision} the action.`);
    } finally {
      setDeciding(null);
    }
  };

  const startEdit = (action) => {
    const d = action.params?.details || {};
    let value = isoToLocalInput(d.proposed_slot_local);
    if (!value && d.pickup_date) {
      value = `${d.pickup_date}T${(d.preferred_time || '09:00').slice(0, 5)}`;
    }
    setEditValue(value);
    setEditError(null);
    setEditUnavailable(null);
    setEditing(action.id);
  };

  const submitEdit = async (action) => {
    if (!editValue) {
      setEditError('Pick a date and time.');
      return;
    }
    try {
      setEditBusy(true);
      setEditError(null);
      setEditUnavailable(null);
      const resp = await API.post(`agent/actions/${action.id}/edit_time/`, { slot: editValue });
      if (resp.data?.available === false) {
        // Time isn't open — the backend emailed the open times; show them here too.
        setEditUnavailable(resp.data);
      } else {
        setMessage(`Updated booking time for "${action.summary}".`);
        setEditing(null);
        await fetchActions();
      }
    } catch (err) {
      console.error('Failed to edit booking time:', err);
      setEditError(err.response?.data?.detail || 'Failed to update the time.');
    } finally {
      setEditBusy(false);
    }
  };

  const pending = actions.filter(a => a.status === 'pending_approval');
  const history = actions.filter(a => a.status !== 'pending_approval');

  const renderRow = (action, isPending) => (
    <tr key={action.id} className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
        {ACTION_LABELS[action.action_type] || action.action_type}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {action.summary}
        <ActionDetails action={action} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
        {action.load_pro_number ? `PRO #${action.load_pro_number}` : '—'}
      </td>
      <td className="px-4 py-3 whitespace-nowrap"><StatusBadge action={action} /></td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {action.created_at ? new Date(action.created_at).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {isPending ? (
          <div className="flex gap-2">
            <button
              onClick={() => decide(action, 'approve')}
              disabled={deciding === action.id}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 cursor-pointer"
            >
              <CheckCircle size={14} />
              {deciding === action.id ? 'Working…' : 'Approve'}
            </button>
            {action.action_type === 'book_pickup' && (
              <button
                onClick={() => (editing === action.id ? setEditing(null) : startEdit(action))}
                disabled={deciding === action.id}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 cursor-pointer"
              >
                <Clock size={14} />
                Edit time
              </button>
            )}
            <button
              onClick={() => decide(action, 'reject')}
              disabled={deciding === action.id}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 cursor-pointer"
            >
              <XCircle size={14} />
              Reject
            </button>
          </div>
        ) : (
          <span className="text-sm text-gray-500">
            {action.decided_by_username ? `by ${action.decided_by_username}` : ''}
            {isAutoApplied(action) ? 'by agent, from email' : ''}
            {action.error_message && action.status === 'failed' ? ` — ${action.error_message}` : ''}
          </span>
        )}
      </td>
    </tr>
  );

  const renderEditor = (action) => (
    <tr key={`edit-${action.id}`} className="border-b border-gray-100 bg-blue-50/40">
      <td colSpan={6} className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            New pickup time (warehouse local):
          </label>
          <input
            type="datetime-local"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            onClick={() => submitEdit(action)}
            disabled={editBusy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
          >
            {editBusy ? 'Checking…' : 'Check & save'}
          </button>
          <button
            onClick={() => { setEditing(null); setEditUnavailable(null); setEditError(null); }}
            disabled={editBusy}
            className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          We check Opendock for this exact time. If it’s open it’s pinned for booking; if not,
          we’ll email you the open times for that date.
        </p>
        {editError && <p className="mt-2 text-sm text-red-700">{editError}</p>}
        {editUnavailable && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-sm text-amber-800">{editUnavailable.detail}</p>
            {editUnavailable.available_slots?.length > 0 ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {editUnavailable.available_slots.map(iso => (
                  <button
                    key={iso}
                    onClick={() => { setEditValue(isoToLocalInput(iso)); setEditUnavailable(null); }}
                    className="px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer"
                  >
                    {formatSlotLocal(iso)}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-amber-700">No open times on that date.</p>
            )}
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert size={22} className="text-amber-600" />
        <h1 className="text-2xl font-bold text-gray-900">Agent Approvals</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        High-risk actions proposed by the pickup-coordination agent. Nothing is booked or
        committed until you approve it here. Load updates the agent reads from emails it’s
        CC’d on are applied automatically and listed under history with their before and
        after values; unusual ones (large rate changes, invoiced loads) wait here instead.
      </p>

      {message && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm py-8">Loading agent actions…</div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700">
                Pending approval ({pending.length})
              </h2>
            </div>
            {pending.length === 0 ? (
              <div className="px-4 py-8 text-sm text-gray-500 text-center">
                No actions waiting for approval.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Action', 'Summary', 'Load', 'Status', 'Proposed', ''].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {pending.map(a => (
                      <React.Fragment key={a.id}>
                        {renderRow(a, true)}
                        {editing === a.id && renderEditor(a)}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-6">
            <button
              onClick={() => setShowHistory(v => !v)}
              className="text-sm text-blue-700 hover:text-blue-900 cursor-pointer"
            >
              {showHistory ? 'Hide' : 'Show'} decided &amp; executed actions ({history.length})
            </button>
            {showHistory && history.length > 0 && (
              <div className="mt-3 bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Action', 'Summary', 'Load', 'Status', 'Proposed', 'Decision'].map(h => (
                          <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {history.map(a => renderRow(a, false))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AgentApprovals;
